import React from 'react';
import SectionCard from './SectionCard';
import { formatPercent, plural } from '../utils/narrative';

const W = 500;
const H = 176;
const PAD_LEFT = 34;
const PAD_RIGHT = 10;
const PLOT_TOP = 14;
const PLOT_BOTTOM = 132;

// Quiz scores over time. `recentQuizScores` arrives newest-first and only
// covers quizzes that were passed, so the card says so rather than drawing a
// pass mark it can't back up (the pass threshold varies per quiz).
export default function QuizTrendCard({ data, isVisible }) {
  const raw = Array.isArray(data?.recentQuizScores) ? data.recentQuizScores : [];
  const scores = [...raw].reverse().map((s) => Math.round(s * 100));
  if (scores.length < 2) return null;

  const min = Math.min(...scores);
  const lo = Math.max(0, Math.min(90, Math.floor((min - 5) / 10) * 10));
  const hi = 100;
  const span = hi - lo || 1;

  const x = (i) => PAD_LEFT + (i / (scores.length - 1)) * (W - PAD_LEFT - PAD_RIGHT);
  const y = (value) => PLOT_BOTTOM - ((value - lo) / span) * (PLOT_BOTTOM - PLOT_TOP);

  const ticks = [hi, Math.round((hi + lo) / 2), lo];
  const points = scores.map((value, i) => ({ x: x(i), y: y(value), value }));
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  const passed = data?.quizzesCompleted ?? 0;
  const average = formatPercent(data?.averageQuizScore);
  const passRate = formatPercent(data?.quizPassRate);

  return (
    <SectionCard
      title="Quiz scores over time"
      meta={`Last ${scores.length} passed ${plural(scores.length, 'quiz', 'quizzes')}`}
      lede={`Each point is a quiz you passed, oldest on the left.${
        average ? ` Your scores sit around ${average}.` : ''
      }`}
      footer="Scores are recorded for passed quizzes only, so the line never dips below a pass. The pass rate above counts every attempt."
    >
      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 16 }}>
        {average && (
          <div>
            <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: 'var(--text-primary)' }}>
              {average}
            </div>
            <div style={{ marginTop: 3, fontSize: 12, color: 'var(--text-tertiary)' }}>Average score</div>
          </div>
        )}
        {passed > 0 && (
          <div>
            <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: 'var(--text-primary)' }}>
              {passed}
            </div>
            <div style={{ marginTop: 3, fontSize: 12, color: 'var(--text-tertiary)' }}>
              {plural(passed, 'Quiz', 'Quizzes')} passed{passRate ? ` · ${passRate} of attempts` : ''}
            </div>
          </div>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label={`Line chart of your last ${scores.length} passed quiz scores, from ${scores[0]}% to ${scores[scores.length - 1]}%.`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={PAD_LEFT} y1={y(tick)} x2={W - PAD_RIGHT} y2={y(tick)} stroke="var(--border-subtle)" strokeWidth="1" />
            <text x="0" y={y(tick) + 3.5} fill="var(--text-tertiary)" fontSize="10" fontFamily="var(--font-mono)">
              {tick}
            </text>
          </g>
        ))}

        <path
          d={path}
          fill="none"
          stroke="var(--accent-primary)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 1400,
            strokeDashoffset: isVisible ? 0 : 1400,
            transition: 'stroke-dashoffset 1s ease 0.3s',
          }}
        />

        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="4"
            fill="var(--accent-primary)"
            stroke="var(--bg-elevated)"
            strokeWidth="2"
            style={{ opacity: isVisible ? 1 : 0, transition: `opacity 0.25s ease ${0.6 + i * 0.05}s` }}
          >
            <title>{`Quiz ${i + 1} of ${scores.length}: ${p.value}%`}</title>
          </circle>
        ))}

        <text x={PAD_LEFT} y={H - 8} fill="var(--text-tertiary)" fontSize="10.5" fontFamily="var(--font-mono)">
          Oldest
        </text>
        <text x={W - PAD_RIGHT} y={H - 8} textAnchor="end" fill="var(--text-tertiary)" fontSize="10.5" fontFamily="var(--font-mono)">
          Most recent
        </text>
      </svg>
    </SectionCard>
  );
}
